/* =====================================================================
 * Foto-Buchstaben-Poster Editor (PROJ-9) — Wort aus foto-gefüllten Lettern
 * =====================================================================
 * Engine-Basis: kissen.js (Foto-Slots, Crop-Modal, Slot-Liste, Picker) —
 * neu ist die Buchstaben-Maske: Slots bekommen ihre Form per CSS mask-image
 * aus LETTER_MASKS (assets/letter-masks.js, generiert aus Alfa Slab One;
 * 0..1-Pfade, gemeinsame Höhen-Referenz). Slot-Geometrie wird zur Laufzeit
 * aus Wort + Glyph-Aspekten berechnet (lettersRow — alle Wörter quer).
 *
 * Druck: POSTERFLOW (wie Fotoposter, photo.js-Pattern) — Render-Job beim
 * Cart-Add, _print_image_url in die Order, Exporteo holt ab. KEIN _Provider!
 * Server-Endpoint /api/v1/letterposter/render|data — Backend = PROJ-9 Block 3
 * (bis dahin Phase-3-Fallback wie beim Kissen: 404 toleriert, URL deterministisch).
 * ===================================================================== */

(function () {
  'use strict';

  const BACKEND_API_URL = window.LETTERPOSTER_BACKEND_API_URL || 'https://tiles.distantlines.de';
  const MAX_FILE_SIZE_MB = 20;
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
  const CROPPER_CSS_URL = 'https://cdn.jsdelivr.net/npm/cropperjs@1.6.2/dist/cropper.min.css';
  const CROPPER_JS_URL = 'https://cdn.jsdelivr.net/npm/cropperjs@1.6.2/dist/cropper.min.js';
  const MASK_FONT = 'alfaslab';
  const AMP_COLOR = '#d9a8a0';   // Deko-"&" (DU&ICH) — Akzent-Rosé, kein Foto-Slot

  /* ===================================================================
   * LAYOUT-CONFIGS — die 6 V1-Wörter. Geometrie-Sollwerte aus der
   * abgenommenen Preview (Foto-Buchstaben/preview.html, 2026-06-07).
   * texts: 3 freie Zeilen (script1 oben, script2 + caps unten) — Labels
   * sind nur Platzhalter-Beschriftung, Inhalt ist komplett frei.
   * ===================================================================== */
  function stdTexts(t1, t2, t3) {
    return [
      { content: t1, label: 'Zeile oben (Schreibschrift)', free: true, x: 26, y: 8, font: 'Great Vibes', weight: 400, size: 5.4, color: '#2b2b2b' },
      { content: t2, label: 'Zeile unten (Schreibschrift)', free: true, x: 74, y: 81, font: 'Great Vibes', weight: 400, size: 4.6, color: '#2b2b2b' },
      { content: t3, label: 'Zeile unten (Großbuchstaben)', free: true, x: 50, y: 91, font: 'Playfair Display', weight: 500, size: 2.1, color: '#3a3a3a', upper: true, spacing: '0.32em' }
    ];
  }
  const LAYOUTS = {
    'liebe': {
      id: 'liebe', name: 'LIEBE', word: 'LIEBE', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 22, h: 56, gap: 1.8, maxW: 92 },
      texts: stdTexts('Sophie & Mark', '13. April 2023', 'München')
    },
    'wir': {
      id: 'wir', name: 'WIR', word: 'WIR', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 16, h: 64, gap: 2.6, maxW: 86 },
      texts: stdTexts('Sophie & Mark', '13. April 2023', 'München')
    },
    'du-und-ich': {
      id: 'du-und-ich', name: 'DU&ICH', word: 'DU&ICH', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 21, h: 54, gap: 2.0, maxW: 90 },
      texts: stdTexts('Lisa & Jonas', 'Est. 2019', 'Hamburg')
    },
    'mama': {
      id: 'mama', name: 'MAMA', word: 'MAMA', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 18, h: 60, gap: 2.4, maxW: 88 },
      texts: stdTexts('Emma, Leon & Mia', 'Wir lieben dich', 'Seit 1994')
    },
    'papa': {
      id: 'papa', name: 'PAPA', word: 'PAPA', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 18, h: 60, gap: 2.4, maxW: 88 },
      texts: stdTexts('Emma, Leon & Mia', 'Unser Held', 'Seit 1994')
    },
    'familie': {
      id: 'familie', name: 'FAMILIE', word: 'FAMILIE', format: 'landscape', aspect: '4 / 3',
      blanketColor: '#ffffff',
      row: { y: 22, h: 56, gap: 1.6, maxW: 92 },
      texts: stdTexts('Die Wagners', 'Est. 2016', 'Unser Team')
    }
  };

  const CAM = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';

  /* ===================================================================
   * Buchstaben-Geometrie — MUSS dem Server-Render 1:1 entsprechen
   * (geteilte Quelle: LETTER_MASKS + diese Formeln; Werte = Preview-Soll).
   * =================================================================== */
  function maskURI(d) {
    var svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1' preserveAspectRatio='none'><path d='" + d + "' fill='black'/></svg>";
    return "url(\"data:image/svg+xml," + encodeURIComponent(svg) + "\")";
  }
  function stageRatio(layout) {
    var p = String(layout.aspect).split('/');
    return (parseFloat(p[0]) || 1) / (parseFloat(p[1]) || 1);   // W/H
  }
  /* Querformat: aspekt-treue Breiten, auf maxW% der Stage skaliert, zentriert */
  function lettersRow(layout) {
    var masks = LETTER_MASKS[MASK_FONT];
    var o = layout.row, ar = stageRatio(layout);
    var letters = layout.word.split('');
    var widths = letters.map(function (ch) {
      var m = masks[ch];
      return m ? m.aspect * o.h / ar : 0;
    });
    var total = widths.reduce(function (a, b) { return a + b; }, 0) + o.gap * (letters.length - 1);
    var scale = Math.min(1, o.maxW / total);
    var x = 50 - (total * scale) / 2;
    return letters.map(function (ch, i) {
      var w = widths[i] * scale;
      var box = { ch: ch, x: x, w: w, h: o.h * scale, y: o.y + (o.h - o.h * scale) / 2 };
      x += w + o.gap * scale;
      return box;
    });
  }
  /* photos[] (Foto-Slots, ohne "&") + deco[] ("&" als Akzent-Form) berechnen.
     Alle Wörter QUER, naturgetreue Buchstaben (Entscheidung 07.06. — kein Stretch). */
  function computeLetterSlots(layout) {
    var boxes = lettersRow(layout);
    var photos = [], deco = [];
    boxes.forEach(function (b) {
      if (b.ch === '&') deco.push(b); else photos.push(b);
    });
    return { photos: photos, deco: deco };
  }

  /* ===================================================================
   * Modul-State (single instance — eine Section pro -gestalten-Page)
   * =================================================================== */
  let root, stage, assets, sectionId, layoutId;
  let activeLayout, currentGrid;
  const state = { texts: [], bgColor: '#ffffff', design_color: '#ffffff', paper_size: '', product_type: '', variant_id: '', frame_color: 'brown' };
  const photoSrcs = {};   // slot-index -> objectURL (Vorschau)
  const slotData = {};    // slot-index -> { user_image_url, crop:{x,y,w,h}|null }
  const cropSource = {};  // slot-index -> dataURL/objectURL fürs Crop-Modal

  /* Crop in der Stage anwenden (Spiegel der Server-Logik). crop = 0..1 {x,y,w,h}. */
  function applyCropStyles(img, crop) {
    if (!crop) { img.removeAttribute('style'); return; }
    img.style.inset = 'auto';
    img.style.position = 'absolute';
    img.style.left = (-(crop.x / crop.w) * 100) + '%';
    img.style.top = (-(crop.y / crop.h) * 100) + '%';
    img.style.width = (100 / crop.w) + '%';
    img.style.height = (100 / crop.h) + '%';
    img.style.maxWidth = 'none';
    img.style.objectFit = 'cover';
  }

  /* ===================================================================
   * Stage-Rendering
   * =================================================================== */
  function deepCloneLayout(l) {
    var c = Object.assign({}, l);
    if (l.row) c.row = Object.assign({}, l.row);
    c.texts = l.texts.map(function (t) { return Object.assign({}, t); });
    return c;
  }

  /* Stage-Proportion an die gewählte Postergröße koppeln (Fotoposter-Pattern:
     40x30=4:3, 50x40=5:4, 70x50=7:5, 100x70=10:7). Fotos/Texte/Crops bleiben
     erhalten — nur Geometrie wird neu gerechnet und die Stage neu gerendert. */
  function syncStageToSize() {
    if (!activeLayout || !state.paper_size) return;
    var d = parsePosterDim(state.paper_size);
    var longE = Math.max(d.w, d.h), shortE = Math.min(d.w, d.h);
    var aspect = longE + ' / ' + shortE;
    if (activeLayout.aspect === aspect) return;
    activeLayout.aspect = aspect;
    var slots = computeLetterSlots(activeLayout);
    activeLayout.photos = slots.photos;
    activeLayout.deco = slots.deco;
    renderStage();
  }

  function loadLayout(id) {
    activeLayout = deepCloneLayout(LAYOUTS[id] || LAYOUTS['liebe']);
    if (state.paper_size) {
      var d = parsePosterDim(state.paper_size);
      activeLayout.aspect = Math.max(d.w, d.h) + ' / ' + Math.min(d.w, d.h);
    }
    var slots = computeLetterSlots(activeLayout);
    activeLayout.photos = slots.photos;
    activeLayout.deco = slots.deco;
    state.texts = activeLayout.texts;
    state.bgColor = activeLayout.blanketColor || '#ffffff';
    state.design_color = state.bgColor;
    Object.keys(photoSrcs).forEach(function (k) { delete photoSrcs[k]; });
    Object.keys(slotData).forEach(function (k) { delete slotData[k]; });
    Object.keys(cropSource).forEach(function (k) { delete cropSource[k]; });
    renderStage();
  }

  function renderStage() {
    var layout = activeLayout;
    stage.style.setProperty('--collage-aspect', layout.aspect);
    var wrap = stage.closest('.letterposter-stage-flat');
    if (wrap) wrap.classList.toggle('is-portrait', layout.format === 'portrait');

    var grid = document.createElement('div');
    grid.className = 'collage-grid no-panel';
    grid.style.setProperty('--blanket-color', state.bgColor);
    currentGrid = grid;

    var masks = LETTER_MASKS[MASK_FONT];
    layout.photos.forEach(function (ph, i) {
      var s = document.createElement('div');
      s.className = 'photo-slot letter-slot';
      s.setAttribute('data-slot-index', i);
      // PROJ-31: Glyph am Slot merken - das Crop-Modal zeigt damit die Buchstaben-Silhouette
      s.setAttribute('data-letter', ph.ch || '');
      // Buchstabenform als Maske — skaliert mit der Box, identisch im Print
      var m = masks[ph.ch];
      if (m) {
        s.style.webkitMaskImage = s.style.maskImage = maskURI(m.d);
        s.style.webkitMaskSize = s.style.maskSize = '100% 100%';
        s.style.webkitMaskRepeat = s.style.maskRepeat = 'no-repeat';
      }
      if (photoSrcs[i]) {
        s.classList.add('has-photo');
        s.innerHTML = '<img class="photo" src="' + photoSrcs[i] + '" alt="">';
        var im = s.querySelector('img.photo');
        if (im && slotData[i] && slotData[i].crop) applyCropStyles(im, slotData[i].crop);
      }
      s.title = photoSrcs[i] ? 'Klicken: Foto zuschneiden' : 'Klicken: eigenes Foto laden';
      s.style.position = 'absolute';
      s.style.left = ph.x + '%'; s.style.top = ph.y + '%';
      s.style.width = ph.w + '%'; s.style.height = ph.h + '%';
      s.style.zIndex = 2 + i;
      s.style.cursor = 'pointer';
      s.addEventListener('click', function () {
        if (photoSrcs[i]) openCropModal(i, s); else pickPhotoFor(i, s);
      });
      grid.appendChild(s);

      // CTA (Kamera + "Foto") UNMASKIERT über dem Buchstaben — sonst zerschneidet
      // die Letter-Maske das Icon bis zur Unleserlichkeit (Dominik-Screenshot 07.06.)
      if (!photoSrcs[i]) {
        var cta = document.createElement('div');
        cta.className = 'letter-cta';
        cta.setAttribute('data-cta-for', i);
        cta.style.cssText = 'position:absolute;left:' + ph.x + '%;top:' + ph.y + '%;width:' + ph.w + '%;height:' + ph.h + '%;z-index:' + (40 + i) + ';';
        cta.innerHTML = '<div class="cta">' + CAM + '<span>Foto</span></div>';
        grid.appendChild(cta);
      }
    });

    // Deko-Zeichen ("&" bei DU&ICH): Akzentfarbe statt Foto, nicht klickbar
    (layout.deco || []).forEach(function (d) {
      var m = masks[d.ch];
      if (!m) return;
      var el = document.createElement('div');
      el.className = 'letter-deco';
      el.style.cssText = 'position:absolute;left:' + d.x + '%;top:' + d.y + '%;width:' + d.w + '%;height:' + d.h + '%;background:' + AMP_COLOR + ';z-index:2;';
      el.style.webkitMaskImage = el.style.maskImage = maskURI(m.d);
      el.style.webkitMaskSize = el.style.maskSize = '100% 100%';
      el.style.webkitMaskRepeat = el.style.maskRepeat = 'no-repeat';
      grid.appendChild(el);
    });

    state.texts.forEach(function (t, ti) {
      var el = document.createElement('div');
      el.className = 'text-slot';
      el.setAttribute('data-text-index', ti);
      el.style.fontFamily = "'" + (t.font || 'Playfair Display') + "', serif";
      el.style.fontWeight = t.weight || 400;
      el.style.color = t.color || '#28292d';
      el.style.fontSize = (t.size || 5) + 'cqw';
      if (t.upper) el.style.textTransform = 'uppercase';
      if (t.spacing) el.style.letterSpacing = t.spacing;
      el.textContent = t.content;
      el.style.position = 'absolute';
      el.style.left = (t.x != null ? t.x : 50) + '%';
      el.style.top = (t.y != null ? t.y : 50) + '%';
      el.style.transform = 'translate(-50%, -50%)';
      el.style.zIndex = 7; el.style.whiteSpace = 'nowrap'; el.style.justifyContent = 'center';
      grid.appendChild(el);
    });

    stage.innerHTML = '';
    stage.appendChild(grid);
  }

  /* ===================================================================
   * Foto-Upload pro Slot — objectURL-Sofortvorschau + S3 im Hintergrund
   * (1:1 Kissen-Pattern)
   * =================================================================== */
  let _uploadInput = null;
  function pickPhotoFor(i, slotEl) {
    if (!_uploadInput) {
      _uploadInput = document.createElement('input');
      _uploadInput.type = 'file'; _uploadInput.accept = 'image/jpeg,image/png,image/heic,image/heif';
      _uploadInput.style.display = 'none'; document.body.appendChild(_uploadInput);
    }
    const inp = _uploadInput; inp.value = ''; inp.multiple = false;
    inp.onchange = function () {
      const f = inp.files && inp.files[0];
      if (f) setSlotImage(i, f, slotEl, function () { openCropModal(i, slotEl); });
    };
    inp.click();
  }

  function downscaleForCrop(file, maxSide) {
    return new Promise(function (resolve) {
      const url = URL.createObjectURL(file);
      const im = new Image();
      im.onload = function () {
        const w = im.naturalWidth, h = im.naturalHeight;
        if (!w || !h || (w <= maxSide && h <= maxSide)) { resolve(url); return; }
        const scale = maxSide / Math.max(w, h);
        const c = document.createElement('canvas');
        c.width = Math.round(w * scale); c.height = Math.round(h * scale);
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        resolve(c.toDataURL('image/jpeg', 0.9));
      };
      im.onerror = function () { resolve(url); };
      im.src = url;
    });
  }

  function validateFile(f) {
    if (!ACCEPTED_TYPES.includes(f.type) && !/\.(jpg|jpeg|png|heic|heif)$/i.test(f.name)) {
      alert('Format nicht unterstützt — bitte JPG, PNG oder HEIC'); return false;
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert('Datei zu groß — max ' + MAX_FILE_SIZE_MB + ' MB'); return false;
    }
    return true;
  }

  function setSlotImage(i, file, slotEl, onReady) {
    if (!validateFile(file)) return;
    const url = URL.createObjectURL(file);
    photoSrcs[i] = url;
    cropSource[i] = url;
    slotData[i] = slotData[i] || {};
    slotData[i].crop = null;

    if (!slotEl) slotEl = stage.querySelector('[data-slot-index="' + i + '"]');
    if (slotEl) {
      let img = slotEl.querySelector('img.photo');
      if (!img) {
        slotEl.classList.add('has-photo');
        const cta = slotEl.querySelector('.cta'); if (cta) cta.remove();
        img = document.createElement('img'); img.className = 'photo';
        slotEl.insertBefore(img, slotEl.firstChild);
      }
      img.removeAttribute('style');
      img.src = url;
      slotEl.title = 'Klicken: Foto zuschneiden';
    }
    var ctaOverlay = stage.querySelector('.letter-cta[data-cta-for="' + i + '"]');
    if (ctaOverlay) ctaOverlay.remove();
    refreshSlotListItem(i);

    uploadToS3(file).then(function (publicUrl) {
      slotData[i].user_image_url = publicUrl;
    }).catch(function (err) {
      console.error('[letterposter] S3 upload failed for slot ' + i, err);
    });

    downscaleForCrop(file, 2400).then(function (durl) {
      cropSource[i] = durl;
      if (typeof onReady === 'function') onReady();
    });
  }

  async function uploadToS3(file) {
    const presignedResp = await fetch(BACKEND_API_URL + '/api/v1/upload/presigned-url/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name || 'letter-photo.jpg', content_type: file.type || 'image/jpeg' }),
    });
    if (!presignedResp.ok) throw new Error('Presigned URL failed: HTTP ' + presignedResp.status);
    const presigned = await presignedResp.json();
    const uploadUrl = presigned.uploadUrl || presigned.upload_url;
    const publicUrl = presigned.publicUrl || presigned.public_url;
    const fields = presigned.fields || {};
    if (!uploadUrl || !publicUrl) throw new Error('Invalid presigned response');
    const formData = new FormData();
    Object.keys(fields).forEach(function (k) { formData.append(k, fields[k]); });
    formData.append('file', file);
    const uploadResp = await fetch(uploadUrl, { method: 'POST', body: formData });
    if (!uploadResp.ok && uploadResp.status !== 204) throw new Error('S3 upload failed: HTTP ' + uploadResp.status);
    return publicUrl;
  }

  /* ===================================================================
   * Crop-Modal — 1:1 Kissen/Decken-Pattern (Cropper.js on-demand).
   * Aspekt = Letter-Box-Verhältnis in px (schmales I = schmaler Crop).
   * =================================================================== */
  let _cropper = null, _cropBackdrop = null, _cropSlotIndex = null, _cropInitialRatio = null;
  function ensureCropper() {
    return new Promise(function (resolve, reject) {
      if (window.Cropper) { resolve(); return; }
      if (!document.querySelector('link[data-cropper]')) {
        const lk = document.createElement('link');
        lk.rel = 'stylesheet'; lk.href = CROPPER_CSS_URL; lk.setAttribute('data-cropper', '1');
        document.head.appendChild(lk);
      }
      const sc = document.createElement('script');
      sc.src = CROPPER_JS_URL;
      sc.onload = function () { resolve(); };
      sc.onerror = function () { reject(new Error('Cropper konnte nicht geladen werden')); };
      document.head.appendChild(sc);
    });
  }
  function buildCropBackdrop() {
    const bd = document.createElement('div');
    bd.className = 'collage-cropper-modal-backdrop';
    bd.innerHTML =
      '<div class="collage-cropper-modal">' +
        '<div class="collage-cropper-modal-header"><h3>Foto zuschneiden</h3></div>' +
        '<div class="collage-cropper-wrapper"><img alt=""></div>' +
        '<p class="collage-cropper-help">Verschiebe den Rahmen oder seine Ecken, um dein Bild zuzuschneiden. Mit Mausrad oder Slider kannst du zoomen.</p>' +
        '<div class="collage-cropper-zoom-bar">' +
          '<button type="button" class="collage-zoom-btn collage-zoom-out" aria-label="Verkleinern">−</button>' +
          '<input type="range" class="collage-zoom-slider" min="100" max="200" value="100" aria-label="Zoom">' +
          '<button type="button" class="collage-zoom-btn collage-zoom-in" aria-label="Vergrößern">+</button>' +
        '</div>' +
        '<div class="collage-cropper-modal-footer">' +
          '<button type="button" class="collage-crop-cancel">Abbrechen</button>' +
          '<button type="button" class="primary collage-crop-apply">Zuschneiden &amp; übernehmen</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(bd);
    bd.addEventListener('click', function (e) { if (e.target === bd) closeCropModal(); });
    bd.querySelector('.collage-crop-cancel').addEventListener('click', closeCropModal);
    bd.querySelector('.collage-crop-apply').addEventListener('click', applyCropFromModal);
    const zs = bd.querySelector('.collage-zoom-slider');
    if (zs) zs.addEventListener('input', function () {
      if (!_cropper || !_cropInitialRatio) return;
      _cropper.zoomTo(_cropInitialRatio * (parseInt(this.value, 10) / 100));
    });
    bd.querySelector('.collage-zoom-in').addEventListener('click', function () { if (_cropper) _cropper.zoom(0.1); });
    bd.querySelector('.collage-zoom-out').addEventListener('click', function () { if (_cropper) _cropper.zoom(-0.1); });
    return bd;
  }
  function openCropModal(i, slotEl) {
    _cropSlotIndex = i;
    if (!cropSource[i]) { pickPhotoFor(i, slotEl); return; }
    ensureCropper().then(function () {
      if (!_cropBackdrop) _cropBackdrop = buildCropBackdrop();
      _cropBackdrop.classList.add('visible');
      document.body.style.overflow = 'hidden';
      initCropperImage(i, slotEl);
    }).catch(function (e) { console.error(e); alert('Crop-Editor konnte nicht geladen werden.'); });
  }
  /* PROJ-31 (FamWalls-Muster): Buchstaben-Silhouette IN der Crop-Box.
   * SVG-Overlay mit evenodd-Pfad (Vollrechteck + Glyph) dunkelt alles in der
   * Box ausser dem Buchstaben ab - der Kunde sieht exakt, was im Glyph landet.
   * Die Box-Aspect ist auf das Buchstaben-Verhaeltnis gelockt, das Overlay
   * (preserveAspectRatio=none, 100%/100%) folgt Move/Resize automatisch.
   * Defensiv: fehlt Maske oder Cropper-DOM -> heutiges Verhalten, Croppen
   * funktioniert immer. */
  const CROP_HELP_DEFAULT = 'Verschiebe den Rahmen oder seine Ecken, um dein Bild zuzuschneiden. Mit Mausrad oder Slider kannst du zoomen.';
  const CROP_HELP_LETTER = 'Der helle Bereich zeigt, was im Buchstaben sichtbar wird. Verschiebe den Rahmen oder zoome, bis dein Motiv sitzt.';
  function injectLetterCropMask(ch) {
    if (!_cropBackdrop) return;
    const help = _cropBackdrop.querySelector('.collage-cropper-help');
    const box = _cropBackdrop.querySelector('.cropper-crop-box');
    const old = _cropBackdrop.querySelector('.letter-crop-mask');
    if (old) old.remove();
    const m = ch && LETTER_MASKS[MASK_FONT] && LETTER_MASKS[MASK_FONT][ch];
    if (!box || !m) { if (help) help.textContent = CROP_HELP_DEFAULT; return; }
    const wrap = document.createElement('div');
    wrap.className = 'letter-crop-mask';
    wrap.innerHTML =
      '<svg viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">' +
        '<path fill-rule="evenodd" d="M0 0H1V1H0Z ' + m.d + '" fill="rgba(15,15,15,0.72)"/>' +
        '<path d="' + m.d + '" fill="none" stroke="rgba(255,255,255,0.7)" stroke-width="1.5" vector-effect="non-scaling-stroke"/>' +
      '</svg>';
    box.appendChild(wrap);
    if (help) help.textContent = CROP_HELP_LETTER;
  }

  function initCropperImage(i, slotEl) {
    const img = _cropBackdrop.querySelector('.collage-cropper-wrapper img');
    if (_cropper) { _cropper.destroy(); _cropper = null; }
    let aspect = NaN;
    const el = slotEl || stage.querySelector('[data-slot-index="' + i + '"]');
    if (el) { const r = el.getBoundingClientRect(); if (r.width && r.height) aspect = r.width / r.height; }
    let _built = false;
    img.onload = function () {
      if (_built) return; _built = true;
      requestAnimationFrame(function () {
        _cropper = new Cropper(img, {
          aspectRatio: aspect || NaN,
          viewMode: 1, autoCropArea: 1,
          movable: true, zoomable: true, rotatable: false, scalable: false,
          cropBoxResizable: true, responsive: false, background: false, modal: true,
          dragMode: 'none', toggleDragModeOnDblclick: false, // kein Neu-Aufziehen ausserhalb der Box (Clarity 10.08.)
          minCropBoxWidth: 60, minCropBoxHeight: 60,
          wheelZoomRatio: 0.08,
          ready: function () {
            const cd = _cropper.getCanvasData();
            _cropInitialRatio = cd.naturalWidth ? (cd.width / cd.naturalWidth) : null;
            const zs = _cropBackdrop && _cropBackdrop.querySelector('.collage-zoom-slider');
            if (zs) zs.value = 100;
            const c = slotData[i] && slotData[i].crop;
            if (c) {
              const d = _cropper.getImageData();
              _cropper.setData({ x: c.x * d.naturalWidth, y: c.y * d.naturalHeight, width: c.w * d.naturalWidth, height: c.h * d.naturalHeight });
            }
            // PROJ-31: Buchstaben-Silhouette in die frisch gebaute Crop-Box legen
            injectLetterCropMask(el && el.dataset ? el.dataset.letter : '');
          },
          zoom: function (ev) {
            if (!_cropInitialRatio) return;
            const zs = _cropBackdrop && _cropBackdrop.querySelector('.collage-zoom-slider');
            if (zs) { const pct = Math.round((ev.detail.ratio / _cropInitialRatio) * 100); zs.value = Math.max(100, Math.min(200, pct)); }
          }
        });
      });
    };
    img.src = cropSource[i];
    if (img.complete && img.naturalWidth) img.onload();
  }
  function applyCropFromModal() {
    if (!_cropper || _cropSlotIndex == null) { closeCropModal(); return; }
    const data = _cropper.getData();
    const id = _cropper.getImageData();
    const NW = id.naturalWidth || 1, NH = id.naturalHeight || 1;
    const crop = {
      x: Math.max(0, Math.min(1, data.x / NW)),
      y: Math.max(0, Math.min(1, data.y / NH)),
      w: Math.max(0.01, Math.min(1, data.width / NW)),
      h: Math.max(0.01, Math.min(1, data.height / NH))
    };
    slotData[_cropSlotIndex] = slotData[_cropSlotIndex] || {};
    slotData[_cropSlotIndex].crop = crop;
    const el = stage.querySelector('[data-slot-index="' + _cropSlotIndex + '"]');
    if (el) { const im = el.querySelector('img.photo'); if (im) applyCropStyles(im, crop); }
    closeCropModal();
  }
  function closeCropModal() {
    if (_cropper) { _cropper.destroy(); _cropper = null; }
    if (_cropBackdrop) _cropBackdrop.classList.remove('visible');
    document.body.style.overflow = '';
    _cropSlotIndex = null;
  }

  /* ===================================================================
   * "Deine Fotos" — Pro-Buchstabe-Liste im Layout-Tab (Decken-Pattern)
   * =================================================================== */
  function buildPhotoControls() {
    const host = document.getElementById('letterposter-photo-controls-' + sectionId);
    if (!host) return;
    const ul = document.createElement('ul');
    ul.className = 'collage-photo-slot-list';
    activeLayout.photos.forEach(function (ph, i) {
      const li = document.createElement('li');
      li.className = 'collage-photo-slot-item';
      li.setAttribute('data-slot-index', i);

      const thumb = document.createElement('span');
      thumb.className = 'collage-photo-slot-thumb';
      const timg = document.createElement('img'); timg.alt = '';
      timg.src = photoSrcs[i] || (assets && assets.placeholder) || '';
      thumb.appendChild(timg);

      const label = document.createElement('span');
      label.className = 'collage-photo-slot-label';
      label.textContent = 'Buchstabe ' + ph.ch + ' — Foto ' + (i + 1);

      const btn = document.createElement('label');
      btn.className = 'photo-upload-button collage-photo-slot-upload';
      const span = document.createElement('span');
      span.textContent = photoSrcs[i] ? 'Ändern' : 'Foto hochladen';
      const inp = document.createElement('input');
      inp.type = 'file'; inp.accept = 'image/jpeg,image/png,image/heic,image/heif';
      (function (idx) {
        inp.addEventListener('change', function (e) {
          const f = e.target.files && e.target.files[0];
          if (f) setSlotImage(idx, f, null, function () { openCropModal(idx, null); });
          e.target.value = '';
        });
        thumb.addEventListener('click', function () { if (photoSrcs[idx]) openCropModal(idx, null); });
      })(i);
      btn.appendChild(span); btn.appendChild(inp);

      li.appendChild(thumb); li.appendChild(label); li.appendChild(btn);
      ul.appendChild(li);
    });
    host.innerHTML = '';
    host.appendChild(ul);
  }

  function refreshSlotListItem(i) {
    const li = document.querySelector('.collage-photo-slot-item[data-slot-index="' + i + '"]');
    if (!li) return;
    const ti = li.querySelector('.collage-photo-slot-thumb img');
    if (ti && photoSrcs[i]) ti.src = photoSrcs[i];
    const sp = li.querySelector('.collage-photo-slot-upload span');
    if (sp && photoSrcs[i]) sp.textContent = 'Ändern';
  }

  function buildTextFields() {
    const host = document.getElementById('letterposter-text-fields-' + sectionId);
    if (!host) return;
    host.innerHTML = '';
    state.texts.forEach(function (t, i) {
      const wrap = document.createElement('div'); wrap.className = 'letterposter-text-field';
      const lab = document.createElement('label');
      lab.setAttribute('for', 'letterposter-text-' + i + '-' + sectionId);
      lab.textContent = t.label || 'Text';
      const row = document.createElement('div'); row.className = 'collage-text-row';
      const inp = document.createElement('input');
      inp.type = 'text'; inp.id = 'letterposter-text-' + i + '-' + sectionId;
      inp.value = t.content; inp.autocomplete = 'off'; inp.maxLength = 40;
      inp.addEventListener('input', function () {
        t.content = inp.value;
        const el = stage.querySelector('.text-slot[data-text-index="' + i + '"]');
        if (el) el.textContent = inp.value;
      });
      // Farbfeld pro Zeile — Decken-Engine-Pattern (Dominik 07.06.)
      const col = document.createElement('input');
      col.type = 'color'; col.value = t.color || '#2b2b2b';
      col.addEventListener('input', function () {
        t.color = col.value;
        const el = stage.querySelector('.text-slot[data-text-index="' + i + '"]');
        if (el) el.style.color = col.value;
      });
      row.appendChild(inp); row.appendChild(col);
      wrap.appendChild(lab); wrap.appendChild(row);
      host.appendChild(wrap);
    });
  }

  /* ===================================================================
   * Wort-Picker (Kissen-PROJ-6-Pattern): Thumbnails, wechselt Wort UND
   * Shopify-Produkt (Mapping aus #letterposter-products-<sectionId>).
   * =================================================================== */
  let LAYOUT_PRODUCTS = {};
  function renderLayoutThumb(layout, host) {
    const thumbStage = document.createElement('div');
    thumbStage.className = 'collage-stage is-thumb';
    thumbStage.style.setProperty('--collage-aspect', layout.aspect);
    host.style.backgroundColor = layout.blanketColor || '#ffffff';
    if (layout.format === 'portrait') { thumbStage.style.height = '100%'; thumbStage.style.width = 'auto'; }
    else { thumbStage.style.width = '100%'; thumbStage.style.height = 'auto'; }

    const grid = document.createElement('div');
    grid.className = 'collage-grid no-panel';
    grid.style.setProperty('--blanket-color', layout.blanketColor || '#ffffff');
    const slots = computeLetterSlots(layout);
    const masks = LETTER_MASKS[MASK_FONT];
    slots.photos.concat(slots.deco).forEach(function (b) {
      const m = masks[b.ch];
      if (!m) return;
      const s = document.createElement('div');
      s.style.cssText = 'position:absolute;left:' + b.x + '%;top:' + b.y + '%;width:' + b.w + '%;height:' + b.h + '%;background:' + (b.ch === '&' ? AMP_COLOR : '#d8d2c7') + ';';
      s.style.webkitMaskImage = s.style.maskImage = maskURI(m.d);
      s.style.webkitMaskSize = s.style.maskSize = '100% 100%';
      grid.appendChild(s);
    });
    thumbStage.appendChild(grid);
    host.appendChild(thumbStage);
  }

  function switchProduct(id) {
    const prod = LAYOUT_PRODUCTS[id];
    if (!prod || !prod.variants) return;   // Produkt existiert (noch) nicht → nur Layout wechseln
    // variant_ids/Preise im server-gerenderten Picker-DOM umschreiben (Kissen-Pattern)
    root.querySelectorAll('.tab__switcher a[data-id]').forEach(function (typeLink) {
      var type = typeLink.getAttribute('data-type');
      var fmt = root.querySelector('.el__format[data-id="' + typeLink.getAttribute('data-id') + '"]');
      if (!fmt) return;
      fmt.querySelectorAll('.size__picker a[data-size]').forEach(function (a) {
        var v = prod.variants[type + '|' + a.getAttribute('data-size')];
        if (v) {
          a.setAttribute('data-variant-id', String(v.id));
          if (v.price) a.setAttribute('data-price', String(Math.round(parseFloat(String(v.price).replace(',', '.')) * 100)));
        }
      });
    });
    var current = root.querySelector('.el__format .size__picker a.current[data-variant-id]');
    if (current) state.variant_id = current.getAttribute('data-variant-id');
    if (prod.handle && window.history && history.replaceState) {
      try { history.replaceState(null, '', '/pages/' + prod.handle + '-gestalten'); } catch (e) { /* still */ }
    }
  }

  function buildLayoutPicker() {
    const host = document.getElementById('letterposter-layout-picker-' + sectionId);
    if (!host) return;
    host.innerHTML = '';
    const ul = document.createElement('ul');
    ul.className = 'collage-template-picker';
    Object.keys(LAYOUTS).forEach(function (id) {
      const l = LAYOUTS[id];
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#';
      a.className = 'collage-template' + (id === layoutId ? ' current' : '');
      a.setAttribute('aria-label', l.name);
      const thumb = document.createElement('span');
      thumb.className = 'collage-template-thumb' + (l.format === 'landscape' ? ' is-landscape' : '');
      renderLayoutThumb(l, thumb);
      const name = document.createElement('span');
      name.className = 'collage-template-name';
      name.textContent = l.name;
      a.appendChild(thumb); a.appendChild(name);
      a.addEventListener('click', function (e) {
        e.preventDefault();
        if (id === layoutId) return;
        ul.querySelectorAll('.collage-template').forEach(function (x) { x.classList.remove('current'); });
        a.classList.add('current');
        layoutId = id;
        loadLayout(id);          // resettet Fotos/Texte/Crops (wie Decke/Kissen)
        buildPhotoControls();
        buildTextFields();
        switchProduct(id);
      });
      li.appendChild(a); ul.appendChild(li);
    });
    host.appendChild(ul);
  }

  var formatDefaulted = false;
  function initTabSwitching() {
    const tabs = root.querySelectorAll('.main__switcher > ul > li > a[data-tab]');
    const panels = ['layout__wrapper', 'text__wrapper', 'produkt__wrapper'];
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        const target = tab.dataset.tab;
        tabs.forEach(function (t) { t.classList.remove('current'); });
        tab.classList.add('current');
        panels.forEach(function (cls) {
          const el = root.querySelector('.' + cls);
          if (el) el.style.display = (cls === target) ? '' : 'none';
        });
        // Produkt-Tab-Default (Dominik 21.07.): erster Klick -> Holzrahmen (data-id=2);
        // Schwarz + 50x70 sind im Markup bereits .current.
        if (target === 'produkt__wrapper' && !formatDefaulted) {
          formatDefaulted = true;
          var framed = root.querySelector('.tab__switcher a[data-id="2"]:not(.current)');
          if (framed) framed.click();
        }
        // Mobiles Hochscrollen zur Tab-Leiste (<=991px), alle Optionen sichtbar.
        if (window.matchMedia('(max-width: 991px)').matches) {
          var sw = tab.closest('.main__switcher');
          if (sw) sw.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }
  function initWeiterNav() {
    root.querySelectorAll('.weiter-btn[data-target-tab]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var link = root.querySelector('.main__switcher > ul > li > a[data-tab="' + btn.dataset.targetTab + '"]');
        if (link) link.click();
      });
    });
  }

  /* ===================================================================
   * Produkt-Tab — 1:1 Fotoposter-Schema (photo.js-Konventionen):
   * .tab__switcher a[data-id|data-type] schaltet .el__format + .benefits-box,
   * .size__picker a[data-size|data-variant-id] wählt Variante,
   * a[data-frame-color] die Rahmenfarbe (nur Format 2 sichtbar).
   * =================================================================== */
  function formatPrice(cents) {
    return (parseInt(cents, 10) / 100).toFixed(2).replace('.', ',');
  }
  function syncVariantFromSize(a) {
    state.paper_size = a.getAttribute('data-size') || '';
    state.variant_id = a.getAttribute('data-variant-id') || '';
    var variantInput = document.getElementById('letterposter-variant-id-' + sectionId);
    if (variantInput && state.variant_id) variantInput.value = state.variant_id;
    var priceEl = root.querySelector('.bottom__controls .variant-price');
    var cents = a.getAttribute('data-price');
    if (priceEl && cents) { priceEl.textContent = formatPrice(cents) + ' €'; priceEl.dataset.price = cents; }
    syncStageToSize();
  }
  /* Holzrahmen-Vorschau: bei Typ "Poster im Holzrahmen" Frame an der Stage ein. */
  function updateFramePreview() {
    if (!stage) return;
    var framed = (state.product_type || '').indexOf('Holzrahmen') !== -1;
    if (framed) { stage.classList.add('has-frame'); stage.setAttribute('data-frame-color', state.frame_color || 'brown'); }
    else { stage.classList.remove('has-frame'); stage.removeAttribute('data-frame-color'); }
  }

  function initFormatPickers() {
    var typeLinks = root.querySelectorAll('.tab__switcher a[data-id]');
    var sizeLinks = root.querySelectorAll('.el__format .size__picker a[data-size]');
    var frameLinks = root.querySelectorAll('a[data-frame-color]');

    typeLinks.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        typeLinks.forEach(function (x) { x.classList.remove('current'); });
        a.classList.add('current');
        state.product_type = a.getAttribute('data-type') || '';
        var typeId = a.getAttribute('data-id');
        updateFramePreview();
        root.querySelectorAll('.el__format').forEach(function (f) {
          f.style.display = (f.getAttribute('data-id') === typeId) ? '' : 'none';
        });
        root.querySelectorAll('.benefits-box').forEach(function (b) {
          b.style.display = (b.getAttribute('data-id') === typeId) ? '' : 'none';
        });
        // Größe im neuen Format übernehmen: gewünschte behalten, sonst current/erste
        var fmt = root.querySelector('.el__format[data-id="' + typeId + '"]');
        if (!fmt) return;
        var ziel = fmt.querySelector('.size__picker a[data-size="' + state.paper_size + '"]') ||
                   fmt.querySelector('.size__picker a.current') ||
                   fmt.querySelector('.size__picker a[data-size]');
        if (ziel) {
          fmt.querySelectorAll('.size__picker a').forEach(function (l) { l.classList.remove('current'); });
          ziel.classList.add('current');
          syncVariantFromSize(ziel);
        }
      });
    });

    sizeLinks.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var picker = a.closest('.size__picker');
        picker.querySelectorAll('a').forEach(function (l) { l.classList.remove('current'); });
        a.classList.add('current');
        syncVariantFromSize(a);
      });
    });

    frameLinks.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        frameLinks.forEach(function (x) { x.classList.remove('current'); });
        a.classList.add('current');
        state.frame_color = a.getAttribute('data-frame-color');
        updateFramePreview();
      });
    });

    // Initialzustand aus dem server-gerenderten Markup lesen
    var activeType = root.querySelector('.tab__switcher a.current[data-type]') || typeLinks[0];
    if (activeType) state.product_type = activeType.getAttribute('data-type') || '';
    var activeFrame = root.querySelector('a[data-frame-color].current');
    if (activeFrame) state.frame_color = activeFrame.getAttribute('data-frame-color');
    var visFormat = root.querySelector('.el__format[data-id="1"]');
    var activeSize = visFormat && (visFormat.querySelector('.size__picker a.current') || visFormat.querySelector('.size__picker a[data-size]'));
    if (activeSize) {
      if (!activeSize.classList.contains('current')) activeSize.classList.add('current');
      syncVariantFromSize(activeSize);
    }
    updateFramePreview();
  }

  /* Schriftart-Picker (Fotoposter-Pattern): EINE Auswahl für alle Textzeilen.
     state.texts[].font wird gesetzt → Payload trägt es, Server rendert 1:1. */
  function initFontPicker() {
    var links = root.querySelectorAll('.font-options-grid a[data-font]');
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        links.forEach(function (x) { x.classList.remove('current'); });
        a.classList.add('current');
        var font = a.getAttribute('data-font');
        state.texts.forEach(function (t, i) {
          t.font = font;
          var el = stage.querySelector('.text-slot[data-text-index="' + i + '"]');
          if (el) el.style.fontFamily = "'" + font + "', serif";
        });
      });
    });
  }

  function initColorPicker() {
    const links = root.querySelectorAll('.blanket-color-picker .wrap a');
    const customInput = document.getElementById('letterposter-custom-color-' + sectionId);
    function apply(hex) {
      state.bgColor = hex; state.design_color = hex;
      if (currentGrid) currentGrid.style.setProperty('--blanket-color', hex);
    }
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        if (link.classList.contains('custom')) return;
        e.preventDefault();
        links.forEach(function (l) { l.classList.remove('current'); });
        link.classList.add('current');
        const c = link.getAttribute('data-color');
        if (c) apply(c);
      });
    });
    if (customInput) {
      let deb;
      customInput.addEventListener('input', function () {
        const self = this; const customLink = self.closest('a');
        if (customLink) { links.forEach(function (l) { l.classList.remove('current'); }); customLink.classList.add('current'); }
        clearTimeout(deb); deb = setTimeout(function () { apply(self.value); }, 120);
      });
    }
  }

  /* ===================================================================
   * Cart-Add — POSTERFLOW-Flow (photo.js-Pattern): Render-Job anstoßen,
   * _print_image_url + Config in die Order. KEIN _Provider (≠ MerchOne)!
   * =================================================================== */
  function parsePosterDim(label) {
    const m = /([0-9]+)\s*x\s*([0-9]+)/i.exec(label || '');
    return m ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) } : { w: 30, h: 40 };
  }

  async function handleCartAdd() {
    const cartBtn = document.getElementById('letterposter-cart-btn-' + sectionId);
    if (!state.variant_id) { alert('Bitte wähle Produkt und Größe.'); return; }
    const filled = activeLayout.photos.map(function (_, i) { return i; }).filter(function (i) { return slotData[i] && slotData[i].user_image_url; });
    if (filled.length === 0) {
      alert('Bitte lade mindestens 1 eigenes Foto hoch.');
      return;
    }
    cartBtn.disabled = true; cartBtn.textContent = 'Wird vorbereitet…';
    try {
      const jobId = (crypto && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));

      // print-Block: Server liest NUR hieraus (nie aus dem Größen-Label) —
      // Orientierung ist pro Wort fix (LIEBE portrait, Rest landscape).
      const dim = parsePosterDim(state.paper_size);
      const longEdge = Math.max(dim.w, dim.h), shortEdge = Math.min(dim.w, dim.h);
      const isLandscape = activeLayout.format === 'landscape';

      const photoSlotsOut = {};
      activeLayout.photos.forEach(function (ph, i) {
        photoSlotsOut['p' + (i + 1)] = {
          ch: ph.ch,
          user_image_url: (slotData[i] && slotData[i].user_image_url) || '',
          crop: (slotData[i] && slotData[i].crop) || null
        };
      });
      const payload = {
        job_id: jobId,
        product: 'letterposter',
        layout_id: layoutId,
        word: activeLayout.word,
        paper_size: state.paper_size,
        product_type: state.product_type,
        variant_id: state.variant_id,
        photo_slots: photoSlotsOut,
        // Texte MIT allen Style-Feldern — der Server rendert 1:1 (Editor=Print);
        // damit ist auch eine künftige Font-Auswahl automatisch druckfest.
        text_slots: state.texts.map(function (t) {
          return { content: t.content, x: t.x, y: t.y, font: t.font, size: t.size,
                   weight: t.weight, color: t.color, upper: !!t.upper, spacing: t.spacing || '' };
        }),
        bg_color: state.design_color,
        print: {
          width: isLandscape ? longEdge : shortEdge,
          height: isLandscape ? shortEdge : longEdge,
          dpi: 300,
          orientation: activeLayout.format
        }
      };

      // Render-Trigger (Block 3): existiert noch nicht → 404 OK, Fallback-URL deterministisch.
      let printImageUrl = BACKEND_API_URL + '/api/v1/letterposter/data/' + jobId + '/';
      try {
        const renderResp = await fetch(BACKEND_API_URL + '/api/v1/letterposter/render/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        if (renderResp.ok || renderResp.status === 202) {
          const rd = await renderResp.json().catch(function () { return {}; });
          printImageUrl = rd.print_image_url || printImageUrl;
        }
      } catch (e) { /* Backend Block 3 noch nicht live — Fallback bleibt */ }

      const cartProperties = {
        '_Letter Config JSON': JSON.stringify(payload),
        '_Wort': activeLayout.name,
        '_Product Type': state.product_type,
        '_Paper Size': state.paper_size,
        '_print_image_url': printImageUrl,
        '_job_id': jobId,
        // Cart-Vorschau (PROJ-72): persistenter Downscale vom Render-Worker
        '_preview_image_url': BACKEND_API_URL + '/media/blanket_renders/' + jobId + '_cartpreview.jpg',
      };
      // _Frame Color nur bei Holzrahmen (Exporteo-Partnumber-Rebuild braucht das)
      if (state.frame_color && state.product_type.indexOf('Holzrahmen') !== -1) {
        cartProperties['_Frame Color'] = state.frame_color;
      }
      // Cart-Properties IMMER _-geprefixt -> im Warenkorb versteckt (nur Name/Preis/Variante sichtbar)
      state.texts.forEach(function (t) {
        if (t.content) cartProperties['_' + (t.label || 'Text')] = t.content;
      });
      Object.keys(cartProperties).forEach(function (k) { if (!cartProperties[k]) delete cartProperties[k]; });

      // Sofort-Vorschau (PROJ-72): Schnappschuss der sichtbaren Stage in
      // sessionStorage, damit der Cart-Drawer das Motiv sofort zeigt statt
      // auf den Server-Render zu warten. Helper ist fail-soft (2,5s-Notbremse),
      // .catch sichert zusaetzlich ab: der Kauf blockiert nie.
      const vorschauFertig = (window.kaSofortVorschau
        ? window.kaSofortVorschau(stage, jobId).catch(function () {})
        : Promise.resolve());
      await vorschauFertig;

      const cartResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(state.variant_id, 10), quantity: 1, properties: cartProperties }] }),
      });
      if (!cartResp.ok) throw new Error('Cart-Add HTTP ' + cartResp.status);

      let cartData = null;
      try {
        cartData = await cartResp.json();
        if (window.DLTrack && window.DLTrack.addToCart) window.DLTrack.addToCart(cartData);
      } catch (e) { /* skip tracking */ }

      // Pattern wie photo.js:1782 — theme.js-Listener erwarten detail.cart,
      // ohne detail werfen sie TypeErrors (QA-Befund PROJ-9, 2026-06-07)
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart: cartData } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') cartDrawer.show();
      else window.location.href = '/cart';
    } catch (err) {
      console.error('[letterposter] Cart-add failed', err);
      alert('Konnte nicht in den Warenkorb legen — ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      cartBtn.disabled = false; cartBtn.textContent = 'In den Warenkorb';
    }
  }

  /* ===================================================================
   * Init
   * =================================================================== */
  function init() {
    root = document.querySelector('.letterposter-customizer');
    if (!root || typeof LETTER_MASKS === 'undefined') return;
    sectionId = root.dataset.sectionId;
    layoutId = root.dataset.layoutId || 'liebe';
    stage = document.getElementById('letterposter-stage-' + sectionId);
    assets = { placeholder: root.dataset.assetPlaceholder };

    // Wort→Produkt-Mapping für den Picker (Liquid-JSON)
    try {
      const pj = document.getElementById('letterposter-products-' + sectionId);
      if (pj) LAYOUT_PRODUCTS = JSON.parse(pj.textContent);
    } catch (e) { /* optional — Produkte existieren ggf. noch nicht */ }

    loadLayout(layoutId);
    buildLayoutPicker();
    buildPhotoControls();
    buildTextFields();
    initTabSwitching();
    initWeiterNav();
    initFormatPickers();
    initFontPicker();
    initColorPicker();

    const cartBtn = document.getElementById('letterposter-cart-btn-' + sectionId);
    if (cartBtn) cartBtn.addEventListener('click', handleCartAdd);

    const currentSwatch = root.querySelector('.blanket-color-picker .wrap a.current[data-color]');
    if (currentSwatch) {
      const c = currentSwatch.getAttribute('data-color');
      if (c) { state.bgColor = c; state.design_color = c; if (currentGrid) currentGrid.style.setProperty('--blanket-color', c); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
